import type { PeerId, Transport } from './transport';

/**
 * PEERS, FOR REAL THIS TIME.
 *
 * The WebSocket transport exists because two browsers cannot talk to each other.
 * This one exists because two copies of the Electron build can — Steam's relay
 * network does NAT traversal, holds the connection open, and routes through Valve's
 * datacentres when a direct path cannot be found. No lamp-post, no Cloudflare bill,
 * no room codes: the "address" of a game is a lobby, and the way you hand it to a
 * friend is the overlay.
 *
 * Everything below is a thin adaptor. The real work happens in the main process
 * (`electron/main.cjs`), because `steamworks.js` is a native module and the renderer
 * is sandboxed. What arrives here through the preload bridge is already reduced to
 * the four things the `Transport` contract needs: who am I, who else is here, bytes
 * in, bytes out.
 *
 * WHAT THIS IS NOT is a different netcode. The host is still a peer, still
 * authoritative, still assigning roster slots — `GameNet` cannot tell which
 * transport it is holding, which is the entire reason the interface exists.
 *
 * TESTING, HONESTLY. The relay path is verified in two real browsers against the
 * deployed worker. This path cannot be exercised without two machines, two Steam
 * accounts and an app ID, so it is written to the documented API and to the same
 * contract the other two transports are tested against, and it is not claimed to
 * have been run end to end. The fallback is what makes that acceptable: if Steam is
 * missing or this misbehaves, the desktop build uses the relay and the game still
 * works.
 */

/** What `electron/preload.cjs` puts on the window. Absent in a browser. */
export interface SteamBridge {
  status(): Promise<SteamStatus>;
  host(): Promise<SteamStatus>;
  join(lobbyId: string): Promise<SteamStatus>;
  leave(): Promise<SteamStatus>;
  invite(): Promise<boolean>;
  send(to: string, payload: Uint8Array | string, isText: boolean): void;
  broadcast(payload: Uint8Array | string, isText: boolean): void;
  onPeer(fn: (p: { id: string }) => void): () => void;
  onGone(fn: (p: { id: string; reason: string }) => void): () => void;
  onData(fn: (p: { from: string; bytes?: Uint8Array; text?: string }) => void): () => void;
}

export interface SteamStatus {
  available: boolean;
  /** Why not, when `available` is false. Shown in the lobby so failure is legible. */
  reason?: string;
  appId?: number;
  /** The player's Steam persona name. Beats making them type one in. */
  name?: string;
  self?: string;
  lobbyId?: string | null;
  /** The lobby owner, asked of Steam. The host, in this game's terms. */
  owner?: string | null;
  isHost?: boolean;
  peers?: string[];
}

/** The bridge, or null in any build that is not the desktop one. */
export function steamBridge(): SteamBridge | null {
  return (globalThis as unknown as { steam?: SteamBridge }).steam ?? null;
}

/**
 * A Steam id is eighteen digits and means nothing to a human.
 *
 * The roster shows persona names, so this is only ever seen in the netgraph and in
 * logs — but an unreadable id in a log is a debugging session that takes an hour
 * instead of a minute, so it gets shortened rather than shown raw.
 */
export function shortSteamId(id: string): string {
  return id.length > 8 ? `…${id.slice(-6)}` : id;
}

export class SteamTransport implements Transport {
  readonly stats = { sent: 0, recv: 0, sentMsgs: 0, recvMsgs: 0 };
  readonly self: PeerId;
  readonly isHost: boolean;
  open = true;
  onJoin?: (id: PeerId) => void;
  onLeave?: (id: PeerId, reason: string) => void;
  onMessage?: (from: PeerId, data: ArrayBuffer | string) => void;
  onError?: (message: string) => void;

  private readonly bridge: SteamBridge;
  private readonly owner: PeerId | null;
  private readonly list: PeerId[] = [];
  private readonly off: Array<() => void> = [];
  private readonly enc = new TextEncoder();

  constructor(bridge: SteamBridge, status: SteamStatus) {
    this.bridge = bridge;
    this.self = status.self ?? 'unknown';
    this.isHost = !!status.isHost;
    this.owner = status.owner ?? null;
    // Whoever was already in the lobby when this renderer booted. They are pushed
    // before any handler exists, and announced by `announceExisting` once the
    // caller has wired itself up — the same ordering the relay transport uses, and
    // for the same reason.
    //
    // THE OWNER GOES FIRST, and that is load-bearing. A client with no welcome yet
    // addresses the host as `peers[0]`, which on the relay is safe because a joiner
    // only ever sees the host at that point. A Steam lobby is not like that: the
    // third player to arrive sees two peers, and if the wrong one is first they
    // introduce themselves to another client and wait forever to be let in.
    for (const id of status.peers ?? []) this.list.push(id);
    this.ownerFirst();

    this.off.push(
      bridge.onPeer(({ id }) => {
        if (id === this.self) return;
        if (!this.list.includes(id)) this.list.push(id);
        this.ownerFirst();
        this.onJoin?.(id);
      }),
      bridge.onGone(({ id, reason }) => {
        const i = this.list.indexOf(id);
        if (i >= 0) this.list.splice(i, 1);
        this.onLeave?.(id, reason);
      }),
      bridge.onData(({ from, bytes, text }) => {
        if (text !== undefined) {
          this.stats.recv += this.enc.encode(text).length;
          this.stats.recvMsgs++;
          this.onMessage?.(from, text);
          return;
        }
        if (!bytes) return;
        this.stats.recv += bytes.byteLength;
        this.stats.recvMsgs++;
        // Sliced rather than passed through: the array that crossed IPC may be a
        // view onto a larger buffer, and the binary decoders read from offset zero.
        const copy = bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength,
        ) as ArrayBuffer;
        this.onMessage?.(from, copy);
      }),
    );
  }

  /** Tell the caller about everyone who was already here. Call once, after wiring. */
  announceExisting(): void {
    for (const id of this.list) this.onJoin?.(id);
  }

  get peers(): readonly PeerId[] {
    return this.list;
  }

  send(to: PeerId, data: ArrayBuffer | string): void {
    const [payload, isText, size] = this.frame(data);
    this.stats.sent += size;
    this.stats.sentMsgs++;
    this.bridge.send(to, payload, isText);
  }

  broadcast(data: ArrayBuffer | string): void {
    const [payload, isText, size] = this.frame(data);
    // Counted once per recipient, because unlike the relay this really does put a
    // copy on the wire for each of them — and a netgraph that flatters the host's
    // uplink is exactly the readout you do not want when the host is struggling.
    this.stats.sent += size * Math.max(1, this.list.length);
    this.stats.sentMsgs += Math.max(1, this.list.length);
    this.bridge.broadcast(payload, isText);
  }

  close(): void {
    this.open = false;
    for (const f of this.off) f();
    this.off.length = 0;
    void this.bridge.leave();
  }

  /** Move the lobby owner to the front of the peer list, if they are in it. */
  private ownerFirst(): void {
    if (!this.owner) return;
    const i = this.list.indexOf(this.owner);
    if (i > 0) this.list.unshift(...this.list.splice(i, 1));
  }

  private frame(data: ArrayBuffer | string): [Uint8Array | string, boolean, number] {
    if (typeof data === 'string') return [data, true, this.enc.encode(data).length];
    return [new Uint8Array(data), false, data.byteLength];
  }
}
